import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { useBlogPosts } from "@/hooks/useBlogPosts";
import { format } from "date-fns";

const categoryColors: Record<string, string> = {
  AI: "bg-accent/10 text-accent",
  Agile: "bg-primary/10 text-primary",
  Strategy: "bg-primary/10 text-primary",
};

export function Insights() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { data: blogPosts, isLoading } = useBlogPosts();

  return (
    <section id="insights" ref={ref} aria-labelledby="insights-heading" className="py-20 md:py-28 bg-background">
      <div className="container-wide">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between mb-12"
        >
          <div className="max-w-2xl mb-6 md:mb-0">
            <h2 id="insights-heading" className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">
              Thought Leadership
            </h2>
            <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Insights on Digital Transformation
            </h3>
            <p className="text-lg text-muted-foreground">
              Perspectives on strategy, technology, and leadership in the digital age.
            </p>
          </div>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-primary font-semibold hover:gap-3 transition-all focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-sm"
          >
            View All Articles
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </Link>
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="aspect-[16/10] rounded-xl bg-secondary mb-5" />
                <div className="h-4 bg-secondary rounded w-20 mb-3" />
                <div className="h-6 bg-secondary rounded w-full mb-2" />
                <div className="h-4 bg-secondary rounded w-3/4" />
              </div>
            ))
          ) : (
            blogPosts?.map((post, index) => (
              <Link key={post.id} to={`/blog/${post.id}`}>
                <motion.article
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group cursor-pointer"
                >
                  {/* Featured Image */}
                  <div className="relative aspect-[16/10] rounded-xl bg-secondary mb-5 overflow-hidden">
                    {post.featured_image ? (
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 to-accent/10" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-4xl font-bold text-primary/20">
                            {String(index + 1).padStart(2, "0")}
                          </span>
                        </div>
                      </>
                    )}
                    
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full ${categoryColors[post.category] || "bg-primary/10 text-primary"}`}>
                      {post.category}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" aria-hidden="true" />
                      <span className="sr-only">Reading time:</span>
                      {post.read_time}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="text-xl font-bold text-foreground mb-2 group-hover:text-primary transition-colors">
                    {post.title}
                  </h4>

                  {/* Excerpt */}
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {post.excerpt}
                  </p>

                  {/* Date & CTA */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(post.created_at), "MMM yyyy")}
                    </span>
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
                      Read more
                      <ArrowRight className="w-3 h-3" />
                    </span>
                    <span className="sr-only">Read article: {post.title}</span>
                  </div>
                </motion.article>
              </Link>
            ))
          )}
        </div>
      </div>
    </section>
  );
}
